import React, { useCallback, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';

import Collapsible from '../../library/collapsible/collapsible';
import CollapsibleToggle from '../../library/collapsible/components/collapsible-toggle/collapsible-toggle';
import Explanation from './components/explanation/explanation';
import IntroductionMessage from './components/introduction-message/introduction-message';
import TreadwellAiSolidIcon from '../../shared/icons/treadwell-ai-solid-icon';

import { GET_TREADWELL_GEN_AI } from './graphql/treadwell-product-explanation-queries';

import {
  useCompareProducts,
  useFitment,
  useGraphQLClient,
  useMyStore,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { getVehicleInput } from '../../shared/utils/fitment/fitment';
import { getTreadwellGQLInput } from '../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import { ProductsForComparison } from '../../shared/types/contexts';
import {
  ErrorData,
  Maybe,
  ProductData
} from '../../shared/types/graphql-types';
import { DEFAULT_EXPLANATION_MARKDOWN } from './constants/treadwell-product-explanation-constants';

import './treadwell-product-explanation.scss';

//TODO: Get from graphqlTypes when __FF_TREADWELL_AI_PDP__ & __FF_TREADWELL_AI_COMPARE__ is live
export enum RequestTypeEnum {
  BigCompare = 'BIGCOMPARE',
  ContextualCompare = 'CONTEXTUALCOMPARE',
  Explain = 'EXPLAINABILITY'
}

// TODO: Remove FFProductData & FFRecommendationData  when __FF_TREADWELL_AI_PDP__ & __FF_TREADWELL_AI_COMPARE__ is live
interface FFRecommendationData {
  treadwellSessionResponseCacheKey?: string;
}

interface FFProductData extends Omit<ProductData, 'recommendationData'> {
  recommendationData: ProductData['recommendationData'] & FFRecommendationData;
}

interface TreadwellProductExplanationProps {
  className?: string;
  isComparingProducts?: boolean;
  products?: Array<Maybe<FFProductData>>;
  requestType: RequestTypeEnum;
  showProductComparison?: boolean;
}

function TreadwellProductExplanation(props: TreadwellProductExplanationProps) {
  const {
    className,
    isComparingProducts,
    products = [],
    requestType,
    showProductComparison = false
  } = props;
  const client = useGraphQLClient();
  const {
    clearAllProductsForComparison,
    onProductComparison,
    updateShowCompareDrawer
  } = useCompareProducts();
  const { myStore } = useMyStore();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { selectedFitment } = useFitment();
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState<Maybe<string>>(null);
  const [errors, setErrors] = useState<Maybe<Array<ErrorData>>>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState({});
  const treadwellCacheKey = useMemo(
    () =>
      products?.find(
        product => product?.recommendationData?.treadwellSessionResponseCacheKey
      )?.recommendationData?.treadwellSessionResponseCacheKey,
    [products]
  );

  const shouldUseDefaultExplanation = useMemo(
    () =>
      !isArrayWithLength(products) ||
      !treadwellCacheKey ||
      !treadwellPreferences ||
      !selectedFitment,
    [products, selectedFitment, treadwellPreferences, treadwellCacheKey]
  );
  const fetchCount = useRef(0);
  const collapsedButtonText = shouldUseDefaultExplanation
    ? 'Learn more'
    : 'See why';

  const handleProductComparison = useCallback(() => {
    try {
      const [product1, product2]: Array<FFProductData> = products.reduce<
        Array<FFProductData>
      >((arr, curr) => {
        if (curr) {
          if (curr?.recommendationData?.topRecommendedProductData) {
            arr.unshift(curr);
          } else {
            arr.push(curr);
          }
        }

        return arr;
      }, []);

      if (product1 && product2) {
        clearAllProductsForComparison();
        onProductComparison(product1 as ProductsForComparison, true);
        onProductComparison(product2 as ProductsForComparison, true);
        updateShowCompareDrawer(true);
      } else {
        setErrors([{ message: 'Unable to handle product comparison' }]);
      }
    } catch (err) {
      console.error(err);
      setErrors([{ message: 'Unable to handle product comparison' }]);
    }
  }, [
    clearAllProductsForComparison,
    onProductComparison,
    products,
    updateShowCompareDrawer
  ]);

  const parseMarkdown = useCallback(async markdown => {
    try {
      const parsedMarkdown = await marked.parse(markdown);

      return parsedMarkdown;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  const fetchTreadwellExplanation = useCallback(
    async (forceFetch = false) => {
      if (
        (!shouldUseDefaultExplanation &&
          !isLoading &&
          !explanation &&
          !errors) ||
        forceFetch
      ) {
        const treadwell = getTreadwellGQLInput(
          selectedFitment,
          treadwellPreferences
        );
        const vehicleInfo = getVehicleInput(selectedFitment);

        setIsLoading(true);
        setErrors(null);
        fetchCount.current++;

        const { data, errors } = await client.query({
          fetchPolicy: 'network-only',
          query: GET_TREADWELL_GEN_AI,
          variables: {
            productInfo: products.map(product => ({
              frontProductCode: product!.code,
              rearProductCode: product!.rearProduct?.code
            })),
            requestType: requestType,
            sourceType: 'WEB',
            storeCode: myStore?.code,
            treadwellCacheKey: treadwellCacheKey,
            treadwellRequest: treadwell,
            vehicleInfo
          }
        });

        const explanationData = data?.product?.treadwellGenAI;

        if (explanationData) {
          const parsedExplanationData = await parseMarkdown(explanationData);

          if (parsedExplanationData) {
            setExplanation(parsedExplanationData);
          } else {
            setErrors([{ message: 'Unable to parse explanation JSON' }]);
          }
        } else {
          setErrors(
            errors || [{ message: 'Unable to parse explanation JSON' }]
          );
        }

        setIsLoading(false);
      }
    },
    [
      client,
      errors,
      explanation,
      isLoading,
      myStore?.code,
      parseMarkdown,
      products,
      requestType,
      selectedFitment,
      shouldUseDefaultExplanation,
      treadwellCacheKey,
      treadwellPreferences
    ]
  );

  const handleToggle = useCallback(() => {
    setShowExplanation(!showExplanation);
    fetchTreadwellExplanation();
  }, [fetchTreadwellExplanation, showExplanation]);

  return (
    <div className={className} styleName="container">
      <div styleName="collapsible-toggle-container">
        <TreadwellAiSolidIcon styleName="treadwell-ai-icon" />
        <div styleName="introduction-container">
          <IntroductionMessage
            isComparingProducts={isComparingProducts}
            products={products}
          />
          <CollapsibleToggle
            collapseType="SECONDARY"
            isOpen={showExplanation}
            label={showExplanation ? 'Hide details' : collapsedButtonText}
            styleName="collapsible-toggle"
            toggleCollapsible={handleToggle}
            toggleType="CHEVRON"
          />
        </div>
      </div>
      <Collapsible hideToggle isOpen={showExplanation} styleName="collapsible">
        <Explanation
          errors={errors}
          explanation={
            shouldUseDefaultExplanation
              ? DEFAULT_EXPLANATION_MARKDOWN
              : explanation
          }
          fetchTreadwellExplanation={fetchTreadwellExplanation}
          hideDisclaimer={shouldUseDefaultExplanation}
          hideReviewFeedback={shouldUseDefaultExplanation}
          id={treadwellCacheKey || products[0]?.code || ''}
          isLoading={isLoading}
          onHelpfulClick={(id, isHelpful) => {
            setSubmittedFeedback(prev => ({
              ...prev,
              [id]: isHelpful
            }));
          }}
          onProductComparison={
            showProductComparison &&
            products.length >= 2 &&
            !selectedFitment?.isStaggered
              ? handleProductComparison
              : undefined
          }
          showErrorRefetchButton={fetchCount.current < 2}
          submittedFeedback={submittedFeedback}
        />
      </Collapsible>
    </div>
  );
}

export default TreadwellProductExplanation;
